export enum AnnotationPosition {
  TopLeft = 'top-left',
  TopRight = 'top-right',
  BottomLeft = 'bottom-left',
  BottomRight = 'bottom-right',
}

export const DATE_FORMATS = ['mdyl', 'mdy/', 'dmy/', 'dmy.', 'dmy-', 'ymd-'] as const;
export type AppDateFormat = (typeof DATE_FORMATS)[number];

export const TIME_FORMATS = ['12s', '12sz', '12', '12z', '24s', '24sz', '24', '24z'] as const;
export type AppTimeFormat = (typeof TIME_FORMATS)[number];

/**
 * How to name default albums for users:
 * - `legacy`: `User <email>`
 * - `flavor`: `<UploadCam|SchoolCam> <email>`
 */
export type UserDefaultFolderPrefix = 'legacy' | 'flavor';

/** Nullable value. We don't use `undefined` as Firebase ignores `undefined` values when saving. */
type Nullable<T> = T | null;

export interface TeamSettings {
  'annotations.copyright': string;
  'annotations.customText': string;
  'annotations.enabled': boolean;
  'annotations.fontColor': string;
  'annotations.fontSize': number;
  'annotations.position': AnnotationPosition;
  'annotations.withCopyright': boolean;
  'annotations.withCustomText': boolean;
  'annotations.withDate': boolean;
  'annotations.withFileName': boolean;
  'annotations.withFolderName': boolean;
  'annotations.withGPSLocation': boolean;
  'annotations.withReversedLocation': boolean;
  'annotations.withTags': boolean;
  'annotations.withTime': boolean;
  'annotations.withUsername': boolean;
  'app.allowPersonalStorage': boolean;
  'app.allowsSavingCopyToDevice': boolean;
  'app.allowsViewingUploadsLog': boolean;
  'app.createDomainUsersDefaultAlbum': boolean;
  'app.dateFormat': AppDateFormat;
  'app.autoSwitchToNearbyFolder': boolean;
  'app.detectSignificantLocationChanges': boolean;
  'app.detectSignificantLocationChanges.distance': number;
  'app.timeFormat': AppTimeFormat;
  'app.userDefaultFolderPrefix': UserDefaultFolderPrefix;
  'camera.promptForFileDetails': boolean;
  'camera.aspectRatio': '4:3' | '1:1' | '16:9' | 'user-defined';
  'camera.resolution': 'highest' | 'medium' | 'user-defined';
  'camera.videoRecordingResolution': 'highest' | 'full-hd' | 'hd' | 'user-defined';
  'team.sendAlbumAssignmentEmail': boolean;
  'team.logo': Nullable<string>;
  'team.mainColor': Nullable<string>;
  'team.sendUserInvitationEmail': boolean;
  'team.sendWelcomeEmail': boolean;
}

export const TEAM_PUBLIC_CACHE_SETTINGS = ['team.logo', 'team.mainColor'] as const;
export type TeamPublicCacheSettingsKey = (typeof TEAM_PUBLIC_CACHE_SETTINGS)[number];
export type TeamPublicCacheSettings = Pick<TeamSettings, TeamPublicCacheSettingsKey>;

// We set some hard defaults in `CreateTeamFn` for newly created Teams, check
// those also. The defaults here are for properties that don't exist in
// `TeamEntity.settings`. When user sets a setting on the UI, we only set
// individually changed settings.
export const DEFAULT_TEAM_SETTINGS: Readonly<TeamSettings> = {
  'annotations.copyright': '',
  'annotations.customText': '',
  'annotations.enabled': false,
  'annotations.fontColor': '#ffffff',
  'annotations.fontSize': 1.0,
  'annotations.position': AnnotationPosition.BottomRight,
  'annotations.withCopyright': false,
  'annotations.withCustomText': false,
  'annotations.withDate': true,
  'annotations.withFileName': false,
  'annotations.withFolderName': false,
  'annotations.withGPSLocation': false,
  'annotations.withReversedLocation': false,
  'annotations.withTags': false,
  'annotations.withTime': true,
  'annotations.withUsername': false,
  'app.allowPersonalStorage': true,
  'app.allowsSavingCopyToDevice': false,
  'app.allowsViewingUploadsLog': true,
  'app.createDomainUsersDefaultAlbum': false,
  'app.dateFormat': 'mdy/',
  'app.autoSwitchToNearbyFolder': true,
  'app.detectSignificantLocationChanges': true,
  'app.detectSignificantLocationChanges.distance': 100,
  'app.timeFormat': '12sz',
  'app.userDefaultFolderPrefix': 'legacy',
  'camera.aspectRatio': '4:3',
  'camera.promptForFileDetails': false,
  'camera.resolution': 'highest',
  'camera.videoRecordingResolution': 'full-hd',
  'team.sendAlbumAssignmentEmail': false,
  'team.logo': null,
  'team.mainColor': null,
  'team.sendUserInvitationEmail': true,
  'team.sendWelcomeEmail': true,
};

export const DEFAULT_TEAM_PUBLIC_CACHE_SETTINGS: Readonly<TeamPublicCacheSettings> =
  Object.fromEntries(
    TEAM_PUBLIC_CACHE_SETTINGS.map((key) => [key, DEFAULT_TEAM_SETTINGS[key]]),
  ) as TeamPublicCacheSettings;
